import { get, post, put } from '@classic/Foundation/Services/ApiService'
import type { Ticket } from '@core/Areas/AccountActivity/types'
import type {
  AvatarList,
  FollowMeProfile,
  FollowMeSharedBet,
  FollowMeStats,
} from '@core/Areas/FollowMe/types'
import { CopiedFeedPageSize, FollowFeedPageSize, ShareFeedPageSize } from '../constants'

const enum LocalConstants {
  FollowMeV1ApiBase = '/api/followme/v1',
}

export const postShareToFollowMe = ({ ticketSequence }: { ticketSequence: number }) =>
  post<{ postId: number }>({
    url: `${LocalConstants.FollowMeV1ApiBase}/share/bet`,
    body: { ticketSequence },
  })

export const postMultipleSharesToFollowMe = ({ ticketSequences }: { ticketSequences: number[] }) =>
  post<PostMultipleSharesToFollowMeResponse>({
    url: `${LocalConstants.FollowMeV1ApiBase}/share/bets`,
    body: { ticketSequences },
  })

export const postCreateFollowMeProfile = () =>
  post<CreateProfileResponse>({
    url: `${LocalConstants.FollowMeV1ApiBase}/account/`,
    body: {},
  })

export const getFollowMeSharedBetsFeed = async ({
  pageParam = null,
  sortOrder = 'sortAsc',
  sortFilter = 'JumpTime',
  feedType = 'explore',
  accountId = null,
  fromPostId = undefined,
  pageSize = ShareFeedPageSize,
}: FollowMeFeedParams) => {
  const params = new URLSearchParams()
  params.append('pageSize', (pageSize ?? ShareFeedPageSize).toString())

  const isFollowing = feedType === 'following'

  params.append('following', `${isFollowing}`)

  if (accountId !== null && (feedType === 'pending' || feedType === 'resulted')) {
    params.append('account', accountId)
  }

  if (sortOrder === 'sortAsc') {
    params.append('sortAsc', sortFilter)
  } else {
    params.append('sortDesc', sortFilter)
  }

  if (fromPostId) {
    params.append('from', fromPostId.toString())
    params.append('newPosts', 'true')
  } else if (pageParam !== null) {
    params.append('from', pageParam.toString())
  }

  const url =
    feedType !== 'resulted'
      ? `${LocalConstants.FollowMeV1ApiBase}/share/feed?${params.toString()}`
      : `${LocalConstants.FollowMeV1ApiBase}/share/resulted?${params.toString()}`
  return get<FollowMeSharedBet[]>({ url })
}

export const getCopiedBetsFeed = async ({ pageParam = null }: { pageParam?: number | null }) => {
  const params = new URLSearchParams()
  params.append('pageSize', CopiedFeedPageSize.toString())
  if (pageParam !== null) {
    params.append('from', pageParam.toString())
  }

  const url = `${LocalConstants.FollowMeV1ApiBase}/share/copied?${params.toString()}`
  return get<FollowMeSharedBet[]>({ url })
}

export const getPrincipleFollowMeUserProfile = () => {
  return get<FollowMeProfile>({ url: `${LocalConstants.FollowMeV1ApiBase}/account` })
}

export const getPrincipleFollowMeUserProfileStats = (accountId: string) => {
  return get<FollowMeStats>({
    url: `${LocalConstants.FollowMeV1ApiBase}/account/${accountId}/stats`,
  })
}

export const getFollowMeProfileFollowAccountsPaged = async ({
  pageParam = null,
  accountId = undefined,
  isFollowing = true,
}: {
  pageParam: string | null
  accountId: string | undefined
  isFollowing?: boolean
}) => {
  const params = new URLSearchParams()
  if (pageParam) {
    params.append('from', pageParam.toString())
  }
  if (accountId) {
    params.append('accountId', accountId)
  }
  params.append('pageSize', FollowFeedPageSize.toString())
  const accountFollowUri = isFollowing ? 'following' : 'followed'
  const url = `${LocalConstants.FollowMeV1ApiBase}/account/${accountFollowUri}?${params.toString()}`
  return get<FollowMeProfile[]>({ url })
}

export function postFollowMeFollowAccount(accountId: string) {
  return post<void>({
    url: `${LocalConstants.FollowMeV1ApiBase}/account/follow`,
    body: { followsAccountId: accountId },
  })
}

export function postUnFollowMeFollowAccount(accountId: string) {
  return post<void>({
    url: `${LocalConstants.FollowMeV1ApiBase}/account/unfollow`,
    body: { accountToUnfollowId: accountId },
  })
}

export function postFollowedAccountNotifications(accountId: string, shouldNotify: boolean) {
  return post<void>({
    url: `${LocalConstants.FollowMeV1ApiBase}/account/notification/set`,
    body: { followedAccountId: accountId, notify: shouldNotify },
  })
}

export const getFollowMeUserProfileById = (id: string) =>
  get<FollowMeProfile>({ url: `${LocalConstants.FollowMeV1ApiBase}/account/${id}` })

export const getFollowMeAvatarList = async () => {
  return await get<AvatarList[]>({ url: `${LocalConstants.FollowMeV1ApiBase}/account/avatars` })
}

export const putFollowMeProfileDetails = (body: UpdateProfile) => {
  return put<UpdateProfile>({ url: `${LocalConstants.FollowMeV1ApiBase}/account`, body })
}

export const getFollowMeSharedBetByPostId = (postId: string) =>
  get<FollowMeSharedBet>({ url: `${LocalConstants.FollowMeV1ApiBase}/share/post/${postId}` })

export const getFollowMeCanShareTickets = (ticketSequences: string[]) =>
  post<CanShareResponse[]>({
    url: `${LocalConstants.FollowMeV1ApiBase}/share/tickets/can-share`,
    body: {
      ticketSequences,
    },
  })

// =====
// Types
// =====

type CanShareResponse = {
  canShare: boolean
  ticketSequence: number
}

type CreateProfileResponse = {
  accountId: string
  displayName: string
  description: string
  avatar: string
  color: string
  createdAt: string
}

export interface ProblemResponse {
  detail: string
  instance: string
  exception?: string
  requestId: string
  status: number
  title: string
  traceId: string
  type: string
}

export type UpdateProfile = {
  displayName?: string | null
  description?: string | null
  avatar?: string | null
  color?: string | null
}

export type FollowMeFeedParams = {
  pageParam?: number | null
  pageSize?: number | null
  sortOrder?: string
  sortFilter?: string
  accountId?: string | null
  fromPostId?: number
  feedType: FeedType
}

type PostMultipleSharesToFollowMeResponse = {
  failedShares: number
  successfulShares: number
  totalProcessed: number
  results: (Pick<Ticket, 'ticketSequence'> & {
    errorMessage: string | null
    postId: number | null
    success: boolean
  })[]
}

export type FeedType = 'explore' | 'following' | 'pending' | 'resulted' | 'copied'
